import React from 'react';

import { cssMap } from '@atlaskit/css';
import { FlagsProvider } from '@atlaskit/flag/flags-provider';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled/box';

import { ProductKeys } from '../../../../../common/constants/product-keys';
import type { ThirdPartyConfigsBootstrap } from '../../../../../common/constants/schemas/3p-config';
import { OAuthContainer } from '../../../../../common/utils/oauth-container';
import {
	getSlackAuthUpgradeBanner,
	shouldShowSlackAuthUpgradeBanner,
} from '../../../../../common/utils/oauth-container/slack-auth-upgrade-banner';
import { useBannerEligibleProductsOrder } from '../../../../../controllers/3p-smart-link-usage';
import { useAnalytics } from '../../../../../controllers/analytics';
import { ErrorBoundary } from '../../../../../controllers/error-boundary';
import { useOAuthHandlers } from '../../../../../controllers/oauth';
import { ProductOauthUpgradeBanner } from '../../product-oauth-upgrade-banner';

export type BulkConnectBannerProps = {
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	selectedProduct?: ProductKeys;
	availableProducts: ProductKeys[];
};

// Hide the listitem wrapper when the banner renders nothing to avoid whitespace gaps.
const styles = cssMap({
	hideWhenEmpty: {
		'&:empty': {
			display: 'none',
		},
	},
});

export const BulkConnectBanner = ({
	thirdPartyConfigs,
	selectedProduct,
	availableProducts,
}: BulkConnectBannerProps): React.JSX.Element | null => {
	const { fireAnalyticsEvent } = useAnalytics();
	const bannerEligibleProductsOrder = useBannerEligibleProductsOrder({
		isOnSearchDialog: false,
		thirdPartyConfigs,
		availableProducts,
	});
	const shouldRenderSlackAuthUpgradeBanner =
		!selectedProduct &&
		Boolean(getSlackAuthUpgradeBanner(thirdPartyConfigs)) &&
		shouldShowSlackAuthUpgradeBanner() &&
		fg('ai_3p_slack_enterprise_grid_setup_enabled');

	if (!selectedProduct) {
		const bannerComponent = shouldRenderSlackAuthUpgradeBanner ? (
			<FlagsProvider>
				<ProductOauthUpgradeBanner
					selectedProduct={ProductKeys.Slack}
					thirdPartyConfigs={thirdPartyConfigs}
				/>
			</FlagsProvider>
		) : (
			<OAuthContainer
				thirdPartyConfigs={thirdPartyConfigs}
				eligibleProducts={bannerEligibleProductsOrder}
				useOAuthHandlers={useOAuthHandlers}
				fireAnalyticsEvent={fireAnalyticsEvent}
			/>
		);

		return (
			<ErrorBoundary section="SearchPageRelayResultsOAuthContainer">
				<Box role="listitem" xcss={styles.hideWhenEmpty}>
					{bannerComponent}
				</Box>
			</ErrorBoundary>
		);
	}

	return null;
};
